import type { ZudokuConfig } from "zudoku";
import HomePage from "./src/HomePage";

const config: ZudokuConfig = {
  site: {
    title: "Health Information Exchange API Docs",
    logo: {
      src: { light: "/dha-logo-light.png", dark: "/dha-logo-dark.png" },
      alt: "DHA Logo",
      width: "170px",
    },

    // Footer
    footer: {
      position: "center",
      columns: [
        {
          title: "Resources",
          position: "start",
          links: [
            { label: "Documentation", href: "/introduction" },
            { label: "API Catalog", href: "/catalog" },
          ],
        },
      ],
      copyright: `© ${new Date().getFullYear()} Health Information Exchange. All rights reserved.`,
      logo: {
        src: {
          light: "/dha-logo-light.png",
          dark: "/dha-logo-dark.png",
        },
        alt: "DHA Logo",
        width: "120px",
      },
    },
  },

  // Curated theme from TweakCN
  theme: {
    registryUrl: "https://tweakcn.com/r/themes/cmg7o1i1z000904le18jjh4s8",
  },

  // Publish a .md twin of every docs page at <route>.md.
  //
  // This is what the "Copy page" control and its dropdown actually read. Without it the button
  // fetches a .md URL that does not exist, and because nginx falls back to the SPA shell the
  // fetch still returns 200 - so it silently copies tens of kilobytes of HTML instead. See the
  // matching `location ~ \.(md|txt|json)$` block in nginx.conf, which makes that failure loud.
  //
  // `docs.llms` is deliberately left off. Zudoku's own llms.txt exporter degrades on our content
  // (most pages carry no `title:` frontmatter and it has no H1 fallback), so the index is
  // authored instead - see scripts/llms-sections/.
  docs: {
    publishMarkdown: true,
  },

  // Sidebar and Navigation
  navigation: [
    // Custom Home Page
    {
      type: "custom-page",
      // id: "home",
      label: "Home",
      path: "/",
      element: <HomePage />,
    },

    // Authentication Navigation
    {
      type: "category",
      label: "Authentication",
      collapsible: true, // optional
      collapsed: false, // optional
      icon: "lock",
      items: [
        {
          type: "category",
          label: "Getting Started",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "doc",
              label: "Introduction to Auth",
              file: "docs/authentication/getting-started/intro",
            },
            {
              type: "doc",
              label: "Facility Identification",
              file: "docs/authentication/process/facility-identification",
            },
          ],
        },
        // {
        //   type: "category",
        //   label: "Process Docs",
        //   collapsible: true, // optional
        //   collapsed: false, // optional
        //   items: [
        //     // {
        //     //   type: "doc",
        //     //   label: "OAuth 2.0 Flow",
        //     //   file: "/docs/authentication/process/oauth-flow",
        //     // },
        //     // {
        //     //   type: "doc",
        //     //   label: "API Key Management",
        //     //   file: "/docs/authentication/process/api-keys",
        //     // },
        //   ],
        // },
        // {
        //   type: "category",
        //   label: "Guides",
        //   collapsible: true, // optional
        //   collapsed: false, // optional
        //   items: [
        //     // {
        //     //   type: "doc",
        //     //   label: "Best Practices",
        //     //   file: "/docs/authentication/guides/best-practices",
        //     // },
        //     // {
        //     //   type: "doc",
        //     //   label: "Troubleshooting",
        //     //   file: "/docs/authentication/guides/troubleshooting",
        //     // },
        //   ],
        // },
        {
          type: "category",
          label: "Authentication API reference",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "link",
              label: "Authentication API",
              to: "/auth",
              icon: "key",
              badge: {
                label: "API",
                color: "blue",
              },
            },
          ],
        },
      ],
    },

    // Integration Scenarios Navigation
    {
      type: "category",
      label: "Integration Scenarios",
      collapsible: true,
      collapsed: false,
      icon: "route",
      items: [
        {
          type: "doc",
          label: "Scenarios Overview",
          file: "/docs/scenarios/overview",
        },
        {
          type: "doc",
          label: "Scenario 1: SHIF IP Per Diem",
          file: "/docs/scenarios/scenario-1-shif-ip-per-diem",
        },
        {
          type: "doc",
          label: "Scenario 2: SHIF IP FFS Normal Preauth",
          file: "/docs/scenarios/scenario-2-shif-ip-ffs-normal-preauth",
        },
        {
          type: "doc",
          label: "Scenario 3: SHIF IP FFS Elective Preauth",
          file: "/docs/scenarios/scenario-3-shif-ip-ffs-elective-preauth",
        },
        {
          type: "doc",
          label: "Scenario 4: SHIF OP FFS Elective Preauth",
          file: "/docs/scenarios/scenario-4-shif-op-ffs-elective-preauth",
        },
        {
          type: "doc",
          label: "Scenario 5: SHIF OP FFS Normal Preauth",
          file: "/docs/scenarios/scenario-5-shif-op-ffs-normal-preauth",
        },
        {
          type: "doc",
          label: "Scenario 6: UHC OP Capitation",
          file: "/docs/scenarios/scenario-6-uhc-op-capitation",
        },
        {
          type: "doc",
          label: "Scenario 7: ECCIF Emergency Identified",
          file: "/docs/scenarios/scenario-7-eccif-emergency-identified",
        },
        {
          type: "doc",
          label: "Scenario 8: ECCIF Emergency Unidentified",
          file: "/docs/scenarios/scenario-8-eccif-emergency-unidentified",
        },
      ],
    },

    // Claims & Preauths Navigation
    {
      type: "category",
      label: "Claims & Preauths",
      collapsible: true, // optional
      collapsed: false, // optional
      icon: "file-text",
      items: [
        {
          type: "category",
          label: "Getting Started",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "doc",
              label: "Introduction",
              file: "/docs/claims/getting-started/introduction",
            },
          ],
        },
        {
          type: "category",
          label: "Process Docs",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "category",
              label: "Eligibility",
              collapsible: true,
              collapsed: false,
              items: [
                {
                  type: "doc",
                  label: "Eligibility Process Overview",
                  file: "/docs/claims/process/eligibility/eligibilityProcessOverview",
                },
                {
                  type: "doc",
                  label: "Benefits Coverage",
                  file: "/docs/claims/process/eligibility/benefitsCoverage",
                },
                {
                  type: "doc",
                  label: "Sub-Benefits Coverage",
                  file: "/docs/claims/process/eligibility/subBenefitsCoverage",
                },
                {
                  type: "doc",
                  label: "Interventions Coverage",
                  file: "/docs/claims/process/eligibility/interventionsCoverage",
                },
                {
                  type: "doc",
                  label: "Eligibility Check",
                  file: "/docs/claims/process/eligibility/eligibilityCheck",
                },
                {
                  type: "doc",
                  label: "Patient Utilization",
                  file: "/docs/claims/process/eligibility/patientUtilization",
                },
                {
                  type: "doc",
                  label: "POMSF Balance",
                  file: "/docs/claims/process/eligibility/pomsfBalance",
                },
                {
                  type: "doc",
                  label: "Pre-Visit Authorization",
                  file: "/docs/claims/process/eligibility/preVisitAuthorization",
                },
                {
                  type: "doc",
                  label: "Facility Bed Occupancy",
                  file: "/docs/claims/process/eligibility/bedOccupancy",
                },
              ],
            },
            {
              type: "category",
              label: "Start Visit",
              collapsible: true, // optional
              collapsed: false, // optional
              items: [
                {
                  type: "doc",
                  label: "Start Visit Consent Process Overview",
                  file: "/docs/claims/process/startVisitConsent/startVisitConsentProcessOverview",
                },
                {
                  type: "doc",
                  label: "Start Visit",
                  file: "/docs/claims/process/startVisitConsent/startVisitWorkflow",
                },
              ],
            },
            {
              type: "category",
              label: "Interventions",
              collapsible: true, // optional
              collapsed: false, // optional
              items: [
                {
                  type: "doc",
                  label: "Intervention Process Overview",
                  file: "/docs/claims/process/interventions/interventionProcessOverview",
                },
                {
                  type: "doc",
                  label: "Add Intervention",
                  file: "/docs/claims/process/interventions/addIntervention",
                },
                {
                  type: "doc",
                  label: "Retire Intervention",
                  file: "/docs/claims/process/interventions/retireIntervention",
                },
                {
                  type: "doc",
                  label: "Switch Intervention",
                  file: "/docs/claims/process/interventions/switchIntervention",
                },
                {
                  type: "doc",
                  label: "Restore Intervention",
                  file: "/docs/claims/process/interventions/restoreIntervention",
                },
              ],
            },
            {
              type: "category",
              label: "Preauthorizations",
              collapsible: true, // optional
              collapsed: false, // optional
              items: [
                {
                  type: "doc",
                  label: "Preauths Process Overview",
                  file: "/docs/claims/process/preauths/preauthsProcessOverview",
                },
                {
                  type: "doc",
                  label: "Normal Preauth",
                  file: "/docs/claims/process/preauths/normalPreauths",
                },
                {
                  type: "doc",
                  label: "Elective Preauth",
                  file: "/docs/claims/process/preauths/electivePreauths",
                },
                {
                  type: "doc",
                  label: "Surgical Preauth",
                  file: "/docs/claims/process/preauths/surgicalPreauths",
                },
                {
                  type: "doc",
                  label: "Imaging Preauth",
                  file: "/docs/claims/process/preauths/imagingPreauth",
                },
                {
                  type: "doc",
                  label: "Optical Preauth",
                  file: "/docs/claims/process/preauths/opticalPreauth",
                },
                {
                  type: "doc",
                  label: "Oncology Preauth",
                  file: "/docs/claims/process/preauths/oncologyPreauth",
                },
                {
                  type: "doc",
                  label: "Renal Preauth",
                  file: "/docs/claims/process/preauths/renalPreauth",
                },
                {
                  type: "doc",
                  label: "Cancel Preauth",
                  file: "/docs/claims/process/preauths/cancelPreauth",
                },
                {
                  type: "doc",
                  label: "Remove Preauth Diagnosis",
                  file: "/docs/claims/process/preauths/removePreauthDiagnosis",
                },
                {
                  type: "doc",
                  label: "Remove Preauth Doctor",
                  file: "/docs/claims/process/preauths/removePreauthDoctor",
                },
              ],
            },
            {
              type: "category",
              label: "Billing",
              collapsible: true, // optional
              collapsed: false, // optional
              items: [
                {
                  type: "doc",
                  label: "Billing Process Overview",
                  file: "/docs/claims/process/billing/billingProcessOverview",
                },
                {
                  type: "doc",
                  label: "Set Coverage",
                  file: "/docs/claims/process/billing/setCoverage",
                },
                {
                  type: "doc",
                  label: "Add New Line",
                  file: "/docs/claims/process/billing/addNewLine",
                },
                {
                  type: "doc",
                  label: "Add Combined Billing Details",
                  file: "/docs/claims/process/billing/addCombinedBillingDetails",
                },
                {
                  type: "doc",
                  label: "Remove Line",
                  file: "/docs/claims/process/billing/removeLine",
                },
                {
                  type: "doc",
                  label: "Add diagnosis",
                  file: "/docs/claims/process/billing/addDiagnosis",
                },
                {
                  type: "doc",
                  label: "Remove Diagnosis",
                  file: "/docs/claims/process/billing/removeDiagnosis",
                },
                {
                  type: "doc",
                  label: "Add Attachment",
                  file: "/docs/claims/process/billing/addAttachment",
                },
                {
                  type: "doc",
                  label: "Remove Attachment",
                  file: "/docs/claims/process/billing/removeAttachment",
                },
                {
                  type: "doc",
                  label: "Preview Provider Claim",
                  file: "/docs/claims/process/billing/previewProviderClaim",
                },
                {
                  type: "doc",
                  label: "Preview Payer Claim",
                  file: "/docs/claims/process/billing/previewPayerClaim",
                },
                {
                  type: "doc",
                  label: "Resubmit Claim",
                  file: "/docs/claims/process/billing/resubmitClaim",
                },
                {
                  type: "doc",
                  label: "Edit Claim Line",
                  file: "/docs/claims/process/billing/editClaimLine",
                },
              ],
            },
            {
              type: "category",
              label: "Emergency Claims",
              collapsible: true, // optional
              collapsed: false, // optional
              items: [
                {
                  type: "doc",
                  label: "Emergency Process Overview",
                  file: "/docs/claims/process/emergency/emergencyProcessOverview",
                },
                {
                  type: "doc",
                  label: "Create an Emergency Case Claim",
                  file: "/docs/claims/process/emergency/createEmergencyClaim",
                },
                {
                  type: "doc",
                  label: "Get Emergency Case Protocols",
                  file: "/docs/claims/process/emergency/getEmergencyProtocols",
                },
                {
                  type: "doc",
                  label: "Add Emergency Case Protocols",
                  file: "/docs/claims/process/emergency/addEmergencyProtocols",
                },
                {
                  type: "doc",
                  label: "Add a Doctor to an Emergency Claim",
                  file: "/docs/claims/process/emergency/addEmergencyDoctor",
                },
                {
                  type: "doc",
                  label: "Remove a Doctor from an Emergency Claim",
                  file: "/docs/claims/process/emergency/removeEmergencyDoctor",
                },
                {
                  type: "doc",
                  label: "Resend Doctor Consent",
                  file: "/docs/claims/process/emergency/resendDoctorConsent",
                },
                {
                  type: "doc",
                  label: "Identify an Unknown Emergency Patient",
                  file: "/docs/claims/process/emergency/identifyUnknownPatient",
                },
                {
                  type: "doc",
                  label: "Submit an Emergency Case Claim",
                  file: "/docs/claims/process/emergency/submitEmergencyClaim",
                },
              ],
            },
            {
              type: "category",
              label: "Preauthorization Doctor Consent",
              collapsible: true, // optional
              collapsed: false, // optional
              items: [
                {
                  type: "doc",
                  label: "Preauth Doctor Consent Overview",
                  file: "/docs/claims/process/preauthDoctorConsent/preauthDocConsent",
                },
              ],
            },
            {
              type: "category",
              label: "Claim Dispatch",
              collapsible: true, // optional
              collapsed: false, // optional
              items: [
                {
                  type: "doc",
                  label: "Claim Dispatch Process Overview",
                  file: "/docs/claims/process/claimDispatch/claimDispatchProcessOverview",
                },
                {
                  type: "doc",
                  label: "Inpatient Claim Dispatch",
                  file: "/docs/claims/process/claimDispatch/inPatientClaimDispatch",
                },
                {
                  type: "doc",
                  label: "Outpatient Claim Dispatch",
                  file: "/docs/claims/process/claimDispatch/outPatientClaimDispatch",
                },
                {
                  type: "doc",
                  label: "Close Claim",
                  file: "/docs/claims/process/claimDispatch/closeClaim",
                },
              ],
            },
            {
              type: "category",
              label: "Status Callbacks",
              collapsible: true, // optional
              collapsed: false, // optional
              items: [
                {
                  type: "doc",
                  label: "Callbacks Process Overview",
                  file: "/docs/claims/process/callbacks/callbacksProcessOverview",
                },
                {
                  type: "doc",
                  label: "Register a Callback Endpoint",
                  file: "/docs/claims/process/callbacks/registerCallbackEndpoint",
                },
                {
                  type: "doc",
                  label: "Register a Callback Operation",
                  file: "/docs/claims/process/callbacks/registerCallbackOperation",
                },
                {
                  type: "doc",
                  label: "Manage Callback Endpoints",
                  file: "/docs/claims/process/callbacks/manageCallbackEndpoints",
                },
              ],
            },
            {
              type: "category",
              label: "Remittance",
              collapsible: true, // optional
              collapsed: false, // optional
              items: [
                {
                  type: "doc",
                  label: "Remittance Process Overview",
                  file: "/docs/claims/process/remittances/remittanceProcessOverview",
                },
                {
                  type: "doc",
                  label: "Get Remittances",
                  file: "/docs/claims/process/remittances/getRemittance",
                },
                {
                  type: "doc",
                  label: "Get Claims Paid By Remittance",
                  file: "/docs/claims/process/remittances/getClaimsPaidbyRemittance",
                },
              ],
            },
          ],
        },
        {
          type: "category",
          label: "Guides",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "doc",
              label: "SHA Combination Rules",
              file: "/docs/claims/guides/sha-combination-rules",
            },
            {
              type: "category",
              label: "Special Benefit Rules",
              collapsible: true,
              collapsed: true,
              items: [
                {
                  type: "doc",
                  label: "Overview",
                  file: "/docs/claims/guides/benefit-rules",
                },
                {
                  type: "doc",
                  label: "Maternity",
                  file: "/docs/claims/guides/benefit-rules/maternity",
                },
                {
                  type: "doc",
                  label: "Medical Imaging",
                  file: "/docs/claims/guides/benefit-rules/imaging",
                },
                {
                  type: "doc",
                  label: "Surgical",
                  file: "/docs/claims/guides/benefit-rules/surgical",
                },
                {
                  type: "doc",
                  label: "Critical Care",
                  file: "/docs/claims/guides/benefit-rules/critical-care",
                },
                {
                  type: "doc",
                  label: "Inpatient",
                  file: "/docs/claims/guides/benefit-rules/inpatient",
                },
                {
                  type: "doc",
                  label: "Substance Abuse Rehab",
                  file: "/docs/claims/guides/benefit-rules/substance-abuse-rehab",
                },
              ],
            },
            {
              type: "doc",
              label: "Understanding Benefits and Intervention Codes",
              file: "/docs/claims/guides/benefit-intervention-codes",
            },
            {
              type: "doc",
              label: "Benefit Matrix",
              file: "/docs/claims/guides/benefit-matrix",
            },
            {
              type: "doc",
              label: "Understanding Claims",
              file: "/docs/claims/guides/understandingClaims",
            },
            {
              type: "doc",
              label: "Understanding Claim Statuses",
              file: "/docs/claims/guides/understandingClaimStatuses",
            },
            {
              type: "doc",
              label: "Understanding Preauths",
              file: "/docs/claims/guides/understandingPreauths",
            },
            {
              type: "doc",
              label: "Understanding Preauth Statuses",
              file: "/docs/claims/guides/understandingPreauthStatuses",
            },
            {
              type: "doc",
              label: "PMF Tariffs",
              file: "/docs/claims/guides/pmf-tariffs",
            },
            {
              type: "doc",
              label: "Adding a Claim Doctor",
              file: "/docs/claims/guides/adding-claim-doctor",
            },
            {
              type: "doc",
              label: "Adding Attachments",
              file: "/docs/claims/guides/adding-attachments",
            },
            {
              type: "doc",
              label: "Claim Resubmission",
              file: "/docs/claims/guides/claim-resubmission",
            },
            {
              type: "doc",
              label: "The Deceased Patient Workflow",
              file: "/docs/claims/guides/deceased-workflow",
            },
            {
              type: "doc",
              label: "Partial and Full Discharge",
              file: "/docs/claims/guides/partial-and-full-discharge",
            },
            {
              type: "doc",
              label: "Status Callbacks",
              file: "/docs/claims/guides/status-callbacks",
            },
          ],
        },
        {
          type: "category",
          label: "Claims and Preauths API reference",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "link",
              label: "Claims & Preauths API",
              to: "/eclaims",
              icon: "file-text",
              badge: {
                label: "API",
                color: "blue",
              },
            },
          ],
        },
      ],
    },

    // Consent Services Navigation
    {
      type: "category",
      label: "Consent Services",
      collapsible: true, // optional
      collapsed: false, // optional
      icon: "check-circle",
      items: [
        {
          type: "category",
          label: "Getting Started",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "doc",
              label: "Introduction to Consent",
              file: "docs/consent/getting-started/intro",
            },
          ],
        },
        {
          type: "category",
          label: "Process Docs",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "doc",
              label: "Biometrics Consent",
              file: "docs/consent/process/biometricsConsent",
            },
            {
              type: "doc",
              label: "OTP Fallback: No Usable Fingerprints",
              file: "docs/consent/process/biometricsNotFoundOTPFallback",
            },
            {
              type: "doc",
              label: "Minors Biometrics Consent",
              file: "docs/consent/process/minorsBiometricsConsent",
            },
            {
              type: "doc",
              label: "Minors Biometrics Enrollment",
              file: "docs/consent/process/minorsBiometricsEnrollment",
            },
            {
              type: "doc",
              label: "Get Beneficiary Valid Contact",
              file: "docs/consent/process/getBeneficiaryValidContact",
            },
            {
              type: "doc",
              label: "Send OTP",
              file: "docs/consent/process/sendOTP",
            },
            {
              type: "doc",
              label: "Send Discharge OTP",
              file: "docs/consent/process/sendDischargeOTP",
            },
            {
              type: "doc",
              label: "Get Authorizations",
              file: "docs/consent/process/getAuthorizations",
            },
            {
              type: "doc",
              label: "Reject Authorization",
              file: "docs/consent/process/rejectAuthorization",
            },
            {
              type: "doc",
              label: "Authorization Status Callbacks",
              file: "docs/consent/process/authorizationStatusCallbacks",
            },
            {
              type: "doc",
              label: "OTP Whitelist Request",
              file: "docs/consent/process/createOTPWhitelistRequest",
            },
            {
              type: "doc",
              label: "Get OTP Whitelist Request",
              file: "docs/consent/process/getOTPWhitelistRequest",
            },
          ],
        },
        {
          type: "category",
          label: "Guides",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "doc",
              label: "Biometrics Integration Guide",
              file: "/docs/consent/guides/biometrics-integration",
            },
            {
              type: "doc",
              label: "Minors Biometrics Integration Guide",
              file: "/docs/consent/guides/minors-biometrics-integration",
            },
            {
              type: "doc",
              label: "Minors Biometrics Enrollment Guide",
              file: "/docs/consent/guides/minors-biometrics-enrollment",
            },
          ],
        },
        {
          type: "category",
          label: "Consent Services API reference",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "link",
              label: "Consent Services API",
              to: "/consent",
              icon: "check-circle",
              badge: {
                label: "API",
                color: "blue",
              },
            },
          ],
        },
      ],
    },

    // Registries Navigation
    {
      type: "category",
      label: "Registries",
      collapsible: true, // optional
      collapsed: false, // optional
      icon: "database",
      items: [
        {
          type: "category",
          label: "Getting Started",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "doc",
              label: "Introduction to Registries",
              file: "/docs/registries/gettingStarted/introduction",
            },
          ],
        },
        {
          type: "category",
          label: "Process Docs",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "doc",
              label: "Registries Process Overview",
              file: "/docs/registries/process/registriesProcessOverview",
            },
            {
              type: "doc",
              label: "Patient Search",
              file: "/docs/registries/process/patientSearch",
            },
            {
              type: "doc",
              label: "Facility Search",
              file: "/docs/registries/process/facilitySearch",
            },
            {
              type: "doc",
              label: "Get Healthcare Professional Details",
              file: "/docs/registries/process/professionalSearch",
            },
          ],
        },
        // {
        //   type: "category",
        //   label: "Guides",
        //   collapsible: true, // optional
        //   collapsed: false, // optional
        //   items: [
        //     // {
        //     //   type: "doc",
        //     //   label: "Registry Integration Guide",
        //     //   file: "/docs/registries/guides/understandingRegistries",
        //     // },
        //   ],
        // },
        {
          type: "category",
          label: "Registries API reference",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "link",
              label: "Registries API",
              to: "/registries",
              icon: "database",
              badge: {
                label: "API",
                color: "blue",
              },
            },
          ],
        },
      ],
    },

    // Terminology Service Navigation
    {
      type: "category",
      label: "Terminology Service",
      collapsible: true, // optional
      collapsed: false, // optional
      icon: "book-a",
      items: [
        {
          type: "category",
          label: "Getting Started",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "doc",
              label: "Introduction to Terminology Service",
              file: "/docs/terminologyService/gettingStarted/intro",
            },
          ],
        },
        {
          type: "category",
          label: "Process Docs",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "doc",
              label: "Search Hierarchical Concepts",
              file: "/docs/terminologyService/process/searchHierarchicalConcepts",
            },
            {
              type: "doc",
              label: "Fetch Concept Mappings",
              file: "/docs/terminologyService/process/fetchConceptMappings",
            },
          ],
        },
        // {
        //   type: "category",
        //   label: "Guides",
        //   collapsible: true, // optional
        //   collapsed: false, // optional
        //   items: [
        //     // To add terminology service guides here
        //   ],
        // },
        {
          type: "category",
          label: "Terminology Service API reference",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "link",
              label: "Terminology Service API",
              to: "/terminology-service",
              icon: "book-a",
              badge: {
                label: "API",
                color: "blue",
              },
            },
          ],
        },
      ],
    },

    // Shared Health Record Navigation
    {
      type: "category",
      label: "Shared Health Record",
      collapsible: true, // optional
      collapsed: false, // optional
      icon: "heart-pulse",
      items: [
        {
          type: "category",
          label: "Getting Started",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "doc",
              label: "Introduction to the Shared Health Record",
              file: "/docs/sharedHealthRecord/gettingStarted/intro",
            },
          ],
        },
        {
          type: "category",
          label: "Shared Health Record API reference",
          collapsible: true, // optional
          collapsed: false, // optional
          items: [
            {
              type: "link",
              label: "Shared Health Record API",
              to: "/sharedhealthrecord",
              icon: "heart-pulse",
              badge: {
                label: "API",
                color: "blue",
              },
            },
          ],
        },
      ],
    },

    // Changelog
    {
      type: "doc",
      file: "changelog",
      label: "Changelog",
      icon: "history",
    },

    // API Catalog Navigation
    {
      type: "link",
      to: "/catalog",
      label: "API Catalog",
      icon: "database",
      badge: {
        label: "New",
        color: "purple",
      },
    },
  ],
  redirects: [],

  // APIs for referencing
  apis: [
    // From catalog these will be APIs here
    /* 
    Proposed Outline
      1. Onboarding
      2. Authentication
      3. Claims and Billing
      4. ePrescription
      5. National Registries
      6. Clinical Orders
      7. Shared Health Record
      8. Referrals
      9. Consent
      10. Terminology Service
      11. Notifications
      12. Webhooks
      13. File Management
    */

    // HIE Middleware UAT APIS
    {
      type: "file",
      input: "./apis/Auth-Services/auth.json",
      path: "/auth",
      categories: [{ label: "Auth", tags: ["Authentication"] }],
    },
    {
      type: "file",
      input: "./apis/HIE-Registries/hieRegistry.json",
      path: "/registries",
      categories: [{ label: "Registries", tags: ["HIE Registry"] }],
    },
    {
      type: "file",
      input: "./apis/eClaims-And-Preauth/eClaims.json",
      path: "/eclaims",
      categories: [{ label: "eClaims and Preauth", tags: ["Claims"] }],
    },
    {
      type: "file",
      input: "./apis/Consent-Services/consent.json",
      path: "/consent",
      categories: [{ label: "Consent", tags: ["Consent"] }],
    },
    {
      type: "file",
      input: "./apis/SHR-Services/shr.json",
      path: "/sharedhealthrecord",
      categories: [
        {
          label: "Shared Health Record",
          tags: ["SHR Consent", "SHR Records"],
        },
      ],
    },
    {
      type: "file",
      input: "./apis/Terminology-Services/terminology.json",
      path: "/terminology-service",
      categories: [{ label: "Terminology", tags: ["Clinical Concepts"] }],
    },
  ],
  catalogs: {
    path: "/catalog",
    label: "API Catalog",
  },
};

export default config;
